import { Injectable, signal } from '@angular/core';
import { Observable, lastValueFrom, catchError, timeout, retry } from 'rxjs';
import { UploadFile } from '../types';
import { ToastService } from 'src/app/services/toast.service';
import { SalesforceClientService } from '../../services/salesforce-client.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { LoadingController } from '@ionic/angular';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CombinedOrdersContextService } from './combined-order.service';
import { LoggerService } from 'src/app/services/logger.service';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  allFilesUploaded = signal<boolean>(false);
  uploadFiles = signal<UploadFile[]>([]);
  refKey = signal<string>('');
  currentOrderId = signal<string>('');

  private logger: LoggerService = new LoggerService(FileUploadService.name);

  constructor(
    private readonly client: SalesforceClientService,
    private readonly toastService: ToastService,
    private readonly loadingController: LoadingController,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly combinedOrdersContext: CombinedOrdersContextService,
  ) {}

  async handleUploadFile(index: number, isSingleOrder: boolean) {
    const currentOrderFiles = isSingleOrder
      ? this.uploadFiles()
      : this.combinedOrdersContext.getOrderFiles(this.currentOrderId());

    const uploadFile = currentOrderFiles[index];
    const fileName = uploadFile.file.name;

    uploadFile.isUploading = true;

    try {
      const response = await lastValueFrom(
        this.uploadDocument(
          this.refKey(),
          fileName,
          'PORTAL UPLOAD',
          10,
          uploadFile.file,
        ).pipe(
          catchError((error) => {
            this.logger.error({
              message: `${error.message}`,
              identifier: 'movacarpro_error_upload_document',
              method: 'handleUploadFile',
            });

            this.allFilesUploaded.set(false);

            throw error;
          }),
        ),
      );

      if (!response) {
        this.logger.warn({
          message: `Upload failed for ${fileName}: No response received`,
          identifier: 'movacarpro_error_upload_document',
          method: 'handleUploadFile',
        });

        throw new Error(`Upload failed for ${fileName}: No response received`);
      }

      if (isSingleOrder) {
        this.uploadFiles.update((files) =>
          files.filter((f) => f.file.name !== fileName),
        );

        return;
      }

      this.combinedOrdersContext.removeFileFromOrder(
        this.currentOrderId(),
        fileName,
      );
    } catch (error) {
      this.logger.error({
        message: `${error instanceof Error ? error.message : String(error)}`,
        identifier: 'movacarpro_error_upload_document',
        method: 'handleUploadFile',
      });

      this.allFilesUploaded.set(false);

      throw error;
    }
  }

  async handleUploadFiles(refKey: string | null, orderId?: string) {
    if (!refKey) {
      this.logger.warn({
        message: `No refKey provided`,
        identifier: 'movacarpro_error_upload_document',
        method: 'handleUploadFiles',
      });

      return;
    }

    this.refKey.set(refKey);

    if (orderId) {
      this.currentOrderId.set(orderId);
    }

    const isSingleOrder = !orderId;

    const files = isSingleOrder
      ? this.uploadFiles()
      : this.combinedOrdersContext.getOrderFiles(orderId);

    if (files.length === 0) {
      this.logger.warn({
        message: `No files provided`,
        identifier: 'movacarpro_error_upload_document',
        method: 'handleUploadFiles',
      });

      return;
    }

    try {
      this.allFilesUploaded.set(true);

      while (
        (isSingleOrder
          ? this.uploadFiles()
          : this.combinedOrdersContext.getOrderFiles(orderId)
        ).length > 0
      ) {
        try {
          await this.handleUploadFile(0, isSingleOrder);
        } catch (error) {
          this.logger.error({
            message: `${error instanceof Error ? error.message : String(error)}`,
            identifier: 'movacarpro_error_upload_document',
            method: 'handleUploadFiles',
          });

          this.allFilesUploaded.set(false);

          throw error;
        }
      }

      this.allFilesUploaded.set(true);
    } catch (error) {
      this.logger.error({
        message: `${error instanceof Error ? error.message : String(error)}`,
        identifier: 'movacarpro_error_upload_document',
        method: 'handleUploadFiles',
      });

      this.allFilesUploaded.set(false);

      this.toastService.showErrorToast(
        new Error('movacarfleet_orders_files_error_uploading_file'),
      );
    }
  }

  async reUploadOrderFiles() {
    this.allFilesUploaded.set(true);

    const loading = await this.loadingController.create({
      message: this.translateService.instant(
        'movacarfleet_orders_uploading_files',
      ),
    });

    await loading.present();

    try {
      await this.handleUploadFiles(this.refKey());
    } catch (error) {
      this.logger.error({
        message: `${error instanceof Error ? error.message : String(error)}`,
        identifier: 'movacarpro_error_upload_document',
        method: 'reUploadOrderFiles',
      });

      this.allFilesUploaded.set(false);

      this.toastService.showErrorToast(
        new Error('movacarfleet_orders_files_error_uploading_file'),
      );
    }

    await loading.dismiss();

    if (this.allFilesUploaded()) {
      this.router.navigate(['/fleet/orders/']);
    }
  }

  uploadDocument(
    refKey: string,
    name: string,
    documentType: string,
    amount: number,
    file: File | { url: string; name: string },
  ): Observable<any> {
    let params = new HttpParams()
      .set('refKey', refKey)
      .set('name', name)
      .set('documentType', documentType)
      .set('amount', amount.toString());

    let headers = new HttpHeaders().set(
      'Content-Type',
      'application/octet-stream',
    );

    return this.client
      .request('POST', '/v1/supplierDocument', {
        params: params,
        headers: headers,
        body: file,
      })
      .pipe(
        catchError((error) => {
          this.logger.error({
            message: `${error.message}`,
            identifier: 'movacarpro_error_upload_document',
            method: 'uploadDocument',
          });

          throw error;
        }),
      );
  }
}
